import type {
  DataResponse,
  PaginatedResponse,
  SopCategory,
  SopListItem,
  SopDetail,
  SopDocument,
  SopAssignment,
  SopComplianceSummary,
  MySopItem,
  SopStats,
  CreateSopCategoryInput,
  UpdateSopCategoryInput,
  CreateSopInput,
  UpdateSopInput,
  SopStatusCode,
  ListSopQuery,
  CreateSopAssignmentsInput,
  ListMySopQuery,
} from '@pms/shared';
import { api } from '@/lib/api';

export const sopService = {
  async stats(): Promise<SopStats> {
    const res = await api.get<DataResponse<SopStats>>('/sop/stats');
    return res.data.data;
  },

  // ── Categories ──
  async listCategories(): Promise<SopCategory[]> {
    const res = await api.get<DataResponse<SopCategory[]>>('/sop/categories');
    return res.data.data;
  },
  async createCategory(input: CreateSopCategoryInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/sop/categories', input);
    return res.data.data;
  },
  async updateCategory(id: string, input: UpdateSopCategoryInput): Promise<void> {
    await api.patch(`/sop/categories/${id}`, input);
  },
  async deleteCategory(id: string): Promise<void> {
    await api.delete(`/sop/categories/${id}`);
  },

  // ── SOPs ──
  async listSops(query: Partial<ListSopQuery> = {}): Promise<PaginatedResponse<SopListItem>> {
    const res = await api.get<PaginatedResponse<SopListItem>>('/sop/sops', { params: query });
    return res.data;
  },
  async getSop(id: string): Promise<SopDetail> {
    const res = await api.get<DataResponse<SopDetail>>(`/sop/sops/${id}`);
    return res.data.data;
  },
  async createSop(input: CreateSopInput): Promise<{ id: string }> {
    const res = await api.post<DataResponse<{ id: string }>>('/sop/sops', input);
    return res.data.data;
  },
  async updateSop(id: string, input: UpdateSopInput): Promise<void> {
    await api.patch(`/sop/sops/${id}`, input);
  },
  async setStatus(id: string, status: SopStatusCode): Promise<void> {
    await api.post(`/sop/sops/${id}/status`, { status });
  },
  async deleteSop(id: string): Promise<void> {
    await api.delete(`/sop/sops/${id}`);
  },

  // ── Documents ──
  async listDocuments(sopId: string): Promise<SopDocument[]> {
    const res = await api.get<DataResponse<SopDocument[]>>(`/sop/sops/${sopId}/documents`);
    return res.data.data;
  },
  async uploadDocument(sopId: string, file: File): Promise<SopDocument> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post<DataResponse<SopDocument>>(`/sop/sops/${sopId}/documents`, form);
    return res.data.data;
  },
  async downloadDocument(docId: string): Promise<Blob> {
    const res = await api.get(`/sop/documents/${docId}/download`, { responseType: 'blob' });
    return res.data as Blob;
  },
  async deleteDocument(docId: string): Promise<void> {
    await api.delete(`/sop/documents/${docId}`);
  },

  // ── Assignments ──
  async listAssignments(sopId: string): Promise<SopAssignment[]> {
    const res = await api.get<DataResponse<SopAssignment[]>>(`/sop/sops/${sopId}/assignments`);
    return res.data.data;
  },
  async createAssignments(sopId: string, input: CreateSopAssignmentsInput): Promise<{ created: number }> {
    const res = await api.post<DataResponse<{ created: number }>>(`/sop/sops/${sopId}/assignments`, input);
    return res.data.data;
  },
  async deleteAssignment(assignmentId: string): Promise<void> {
    await api.delete(`/sop/assignments/${assignmentId}`);
  },

  // ── Acknowledgment & compliance ──
  async acknowledge(sopId: string): Promise<{ acknowledged_at: string }> {
    const res = await api.post<DataResponse<{ acknowledged_at: string }>>(`/sop/sops/${sopId}/acknowledge`, {
      confirm: true,
    });
    return res.data.data;
  },
  async listMyAssignments(query: Partial<ListMySopQuery> = {}): Promise<PaginatedResponse<MySopItem>> {
    const res = await api.get<PaginatedResponse<MySopItem>>('/sop/my-assignments', { params: query });
    return res.data;
  },
  async getCompliance(sopId: string): Promise<SopComplianceSummary> {
    const res = await api.get<DataResponse<SopComplianceSummary>>(`/sop/sops/${sopId}/compliance`);
    return res.data.data;
  },
};
